import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, Phone, MapPin, ArrowRight, CheckCircle, Star, Users, Award, Shield, Heart, ChevronDown } from 'lucide-react';
import { OptimizedImage } from '../../components/OptimizedImage';
import { FamilyPhysicianAbout } from './FamilyPhysicianAbout';
import { FamilyPhysicianServices } from './FamilyPhysicianServices';
import { FamilyPhysicianDoctors } from './FamilyPhysicianDoctors';
import { FamilyPhysicianAppointment } from './FamilyPhysicianAppointment';
import { FamilyPhysicianContact } from './FamilyPhysicianContact';
import { FamilyPhysicianResources } from './FamilyPhysicianResources';

export const FamilyPhysician: React.FC = () => {
  const [activePage, setActivePage] = useState<string>('home');

  const renderPage = () => {
    switch (activePage) {
      case 'about':
        return <FamilyPhysicianAbout />;
      case 'services':
        return <FamilyPhysicianServices />;
      case 'doctors':
        return <FamilyPhysicianDoctors />;
      case 'appointment':
        return <FamilyPhysicianAppointment />;
      case 'contact':
        return <FamilyPhysicianContact />;
      case 'resources':
        return <FamilyPhysicianResources />;
      default:
        return renderHomePage();
    }
  };

  const renderNavigation = () => {
    return (
      <nav className="bg-white shadow-md fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <Link to="/templates/family-physician" onClick={() => setActivePage('home')} className="flex-shrink-0 flex items-center">
                <Heart className="h-8 w-8 text-primary-600" />
                <span className="ml-2 text-xl font-bold text-gray-900">Family Physician</span>
              </Link>
            </div>
            
            <div className="hidden md:flex items-center space-x-8">
              <button 
                onClick={() => setActivePage('about')}
                className={`font-medium ${activePage === 'about' ? 'text-primary-600' : 'text-gray-500 hover:text-gray-900'}`}
              >
                About
              </button>
              <button 
                onClick={() => setActivePage('services')}
                className={`font-medium ${activePage === 'services' ? 'text-primary-600' : 'text-gray-500 hover:text-gray-900'}`}
              >
                Services
              </button>
              <button 
                onClick={() => setActivePage('doctors')}
                className={`font-medium ${activePage === 'doctors' ? 'text-primary-600' : 'text-gray-500 hover:text-gray-900'}`}
              >
                Our Doctors
              </button>
              <button 
                onClick={() => setActivePage('resources')}
                className={`font-medium ${activePage === 'resources' ? 'text-primary-600' : 'text-gray-500 hover:text-gray-900'}`}
              >
                Resources
              </button>
              <button 
                onClick={() => setActivePage('contact')}
                className={`font-medium ${activePage === 'contact' ? 'text-primary-600' : 'text-gray-500 hover:text-gray-900'}`}
              >
                Contact
              </button>
            </div>
            
            <div className="hidden md:flex items-center">
              <button 
                onClick={() => setActivePage('appointment')}
                className="bg-primary-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-primary-700 transition-colors"
              >
                Book Appointment
              </button>
            </div>
            
            <div className="md:hidden flex items-center">
              <button className="text-gray-500 hover:text-gray-900">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </nav>
    );
  };

  const renderHomePage = () => {
    return (
      <div>
        {/* Hero Section */}
        <section className="pt-32 pb-20 bg-gradient-to-r from-primary-600 to-accent-500 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="text-4xl sm:text-5xl font-bold mb-6">Compassionate Care for Your Family</h1>
                <p className="text-xl text-white/90 mb-8 leading-relaxed">
                  Welcome to Family Physician Office, where we provide comprehensive healthcare for patients of all ages. 
                  Our team of experienced doctors is dedicated to keeping you and your family healthy.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <button 
                    onClick={() => setActivePage('appointment')}
                    className="bg-white text-primary-600 px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors"
                  >
                    Book an Appointment
                  </button>
                  <button 
                    onClick={() => setActivePage('services')}
                    className="bg-transparent border-2 border-white text-white px-6 py-3 rounded-lg font-medium hover:bg-white/10 transition-colors"
                  >
                    Our Services
                  </button>
                </div>
              </div>
              <div>
                <OptimizedImage
                  src="https://images.pexels.com/photos/7089401/pexels-photo-7089401.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop"
                  alt="Doctor with patient"
                  width={800}
                  height={600}
                  className="rounded-lg shadow-xl"
                  priority={true}
                  fetchPriority="high"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Quick Info */}
        <section className="py-8 bg-white shadow-md relative -mt-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="flex items-center">
                <Clock className="h-8 w-8 text-primary-600 mr-4" />
                <div>
                  <h3 className="font-semibold text-gray-900">Office Hours</h3>
                  <p className="text-gray-600">Mon-Fri: 8am-5pm</p>
                  <p className="text-gray-600">Sat: 9am-12pm</p>
                </div>
              </div>
              <div className="flex items-center">
                <Phone className="h-8 w-8 text-primary-600 mr-4" />
                <div>
                  <h3 className="font-semibold text-gray-900">Contact Us</h3>
                  <p className="text-gray-600">(555) 123-4567</p>
                  <p className="text-gray-600">info@familyphysician.com</p>
                </div>
              </div>
              <div className="flex items-center">
                <MapPin className="h-8 w-8 text-primary-600 mr-4" />
                <div>
                  <h3 className="font-semibold text-gray-900">Location</h3>
                  <p className="text-gray-600">123 Health Street</p>
                  <p className="text-gray-600">Anytown, ST 12345</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Overview */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Services</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                We provide comprehensive healthcare services for your entire family, from newborns to seniors.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-md text-center">
                <div className="mb-4 flex justify-center">
                  <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center">
                    <Shield className="h-8 w-8 text-primary-600" />
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Preventive Care</h3>
                <p className="text-gray-600 mb-4">Regular check-ups, screenings, and immunizations to keep you healthy.</p>
                <button 
                  onClick={() => setActivePage('services')}
                  className="text-primary-600 font-medium hover:text-primary-700 transition-colors"
                >
                  Learn More
                </button>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-md text-center">
                <div className="mb-4 flex justify-center">
                  <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center">
                    <Users className="h-8 w-8 text-primary-600" />
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Family Medicine</h3>
                <p className="text-gray-600 mb-4">Comprehensive care for patients of all ages, from newborns to seniors.</p>
                <button 
                  onClick={() => setActivePage('services')}
                  className="text-primary-600 font-medium hover:text-primary-700 transition-colors"
                >
                  Learn More
                </button>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-md text-center">
                <div className="mb-4 flex justify-center">
                  <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center">
                    <Heart className="h-8 w-8 text-primary-600" />
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Chronic Disease Management</h3>
                <p className="text-gray-600 mb-4">Ongoing care for diabetes, hypertension, asthma, and other conditions.</p>
                <button 
                  onClick={() => setActivePage('services')}
                  className="text-primary-600 font-medium hover:text-primary-700 transition-colors"
                >
                  Learn More
                </button>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-md text-center">
                <div className="mb-4 flex justify-center">
                  <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center">
                    <Calendar className="h-8 w-8 text-primary-600" />
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Telehealth Visits</h3>
                <p className="text-gray-600 mb-4">Virtual appointments for your convenience and safety.</p>
                <button 
                  onClick={() => setActivePage('services')}
                  className="text-primary-600 font-medium hover:text-primary-700 transition-colors"
                >
                  Learn More
                </button>
              </div>
            </div>
            
            <div className="text-center mt-12">
              <button 
                onClick={() => setActivePage('services')}
                className="bg-primary-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-700 transition-colors inline-flex items-center"
              >
                View All Services
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
            </div>
          </div>
        </section>

        {/* Meet Our Doctors */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Meet Our Doctors</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Our team of experienced physicians is dedicated to providing the highest quality care for you and your family.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-gray-50 p-6 rounded-lg shadow-md text-center">
                <OptimizedImage
                  src="https://images.pexels.com/photos/5452201/pexels-photo-5452201.jpeg?auto=compress&cs=tinysrgb&w=300&h=300&fit=crop"
                  alt="Dr. Sarah Johnson"
                  width={150}
                  height={150}
                  className="w-32 h-32 rounded-full mx-auto mb-4 object-cover"
                />
                <h3 className="text-xl font-semibold text-gray-900">Dr. Sarah Johnson</h3>
                <p className="text-primary-600 font-medium mb-2">Family Physician</p>
                <p className="text-gray-600 mb-4">Specializing in preventive care and women's health with over 15 years of experience.</p>
                <button 
                  onClick={() => setActivePage('doctors')}
                  className="text-primary-600 font-medium hover:text-primary-700 transition-colors"
                >
                  Read Bio
                </button>
              </div>
              
              <div className="bg-gray-50 p-6 rounded-lg shadow-md text-center">
                <OptimizedImage
                  src="https://images.pexels.com/photos/5452293/pexels-photo-5452293.jpeg?auto=compress&cs=tinysrgb&w=300&h=300&fit=crop"
                  alt="Dr. Michael Chen"
                  width={150}
                  height={150}
                  className="w-32 h-32 rounded-full mx-auto mb-4 object-cover"
                />
                <h3 className="text-xl font-semibold text-gray-900">Dr. Michael Chen</h3>
                <p className="text-primary-600 font-medium mb-2">Family Physician</p>
                <p className="text-gray-600 mb-4">Focusing on sports medicine and pediatric care with a holistic approach to health.</p>
                <button 
                  onClick={() => setActivePage('doctors')}
                  className="text-primary-600 font-medium hover:text-primary-700 transition-colors"
                >
                  Read Bio
                </button>
              </div>
              
              <div className="bg-gray-50 p-6 rounded-lg shadow-md text-center">
                <OptimizedImage
                  src="https://images.pexels.com/photos/5214958/pexels-photo-5214958.jpeg?auto=compress&cs=tinysrgb&w=300&h=300&fit=crop"
                  alt="Dr. Emily Rodriguez"
                  width={150}
                  height={150}
                  className="w-32 h-32 rounded-full mx-auto mb-4 object-cover"
                />
                <h3 className="text-xl font-semibold text-gray-900">Dr. Emily Rodriguez</h3>
                <p className="text-primary-600 font-medium mb-2">Family Physician</p>
                <p className="text-gray-600 mb-4">Specializing in preventive care and chronic disease management with compassionate care.</p>
                <button 
                  onClick={() => setActivePage('doctors')}
                  className="text-primary-600 font-medium hover:text-primary-700 transition-colors"
                >
                  Read Bio
                </button>
              </div>
            </div>
            
            <div className="text-center mt-12">
              <button 
                onClick={() => setActivePage('doctors')}
                className="bg-primary-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-700 transition-colors inline-flex items-center"
              >
                View All Doctors
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">What Our Patients Say</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Read what our patients have to say about their experience at Family Physician Office.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  "Dr. Johnson is an exceptional physician who truly listens. She takes the time to understand my concerns and explains everything clearly. The entire staff is friendly and professional."
                </p>
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center mr-3">
                    <span className="text-primary-600 font-semibold">JD</span>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">John D.</div>
                    <div className="text-gray-500 text-sm">Patient since 2020</div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  "I've been bringing my family to this practice for years. The doctors are knowledgeable and caring, and the staff always goes above and beyond. I highly recommend them!"
                </p>
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center mr-3">
                    <span className="text-primary-600 font-semibold">SM</span>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Sarah M.</div>
                    <div className="text-gray-500 text-sm">Patient since 2018</div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  "The online appointment booking and patient portal make managing my healthcare so convenient. Dr. Chen is thorough and takes time to answer all my questions."
                </p>
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center mr-3">
                    <span className="text-primary-600 font-semibold">RJ</span>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Robert J.</div>
                    <div className="text-gray-500 text-sm">Patient since 2021</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-primary-600 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold mb-6">Ready to Schedule Your Visit?</h2>
            <p className="text-xl text-primary-100 mb-8">
              We're accepting new patients and would be honored to care for you and your family.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => setActivePage('appointment')}
                className="bg-white text-primary-600 px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors"
              >
                Book an Appointment
              </button>
              <button 
                onClick={() => setActivePage('contact')}
                className="bg-transparent border-2 border-white text-white px-6 py-3 rounded-lg font-medium hover:bg-white/10 transition-colors"
              >
                Contact Us
              </button>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-gray-900 text-white py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <div>
                <div className="flex items-center mb-4">
                  <Heart className="h-8 w-8 text-primary-400" />
                  <span className="ml-2 text-xl font-bold">Family Physician</span>
                </div>
                <p className="text-gray-400 mb-4">
                  Providing comprehensive healthcare for your entire family with compassion and expertise.
                </p>
                <div className="flex space-x-4">
                  <a href="#" className="text-gray-400 hover:text-white transition-colors">
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
                    </svg>
                  </a>
                  <a href="#" className="text-gray-400 hover:text-white transition-colors">
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                    </svg>
                  </a>
                  <a href="#" className="text-gray-400 hover:text-white transition-colors">
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" />
                    </svg>
                  </a>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
                <ul className="space-y-2">
                  <li>
                    <button 
                      onClick={() => setActivePage('home')}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      Home
                    </button>
                  </li>
                  <li>
                    <button 
                      onClick={() => setActivePage('about')}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      About Us
                    </button>
                  </li>
                  <li>
                    <button 
                      onClick={() => setActivePage('services')}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      Services
                    </button>
                  </li>
                  <li>
                    <button 
                      onClick={() => setActivePage('doctors')}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      Our Doctors
                    </button>
                  </li>
                  <li>
                    <button 
                      onClick={() => setActivePage('contact')}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      Contact
                    </button>
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold mb-4">Services</h3>
                <ul className="space-y-2">
                  <li>
                    <button 
                      onClick={() => setActivePage('services')}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      Preventive Care
                    </button>
                  </li>
                  <li>
                    <button 
                      onClick={() => setActivePage('services')}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      Chronic Disease Management
                    </button>
                  </li>
                  <li>
                    <button 
                      onClick={() => setActivePage('services')}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      Women's Health
                    </button>
                  </li>
                  <li>
                    <button 
                      onClick={() => setActivePage('services')}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      Pediatric Care
                    </button>
                  </li>
                  <li>
                    <button 
                      onClick={() => setActivePage('services')}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      Telehealth
                    </button>
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold mb-4">Contact Info</h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <MapPin className="h-5 w-5 text-primary-400 mr-2 mt-0.5" />
                    <span className="text-gray-400">
                      123 Health Street<br />
                      Anytown, ST 12345
                    </span>
                  </li>
                  <li className="flex items-center">
                    <Phone className="h-5 w-5 text-primary-400 mr-2" />
                    <span className="text-gray-400">(555) 123-4567</span>
                  </li>
                  <li className="flex items-center">
                    <Mail className="h-5 w-5 text-primary-400 mr-2" />
                    <span className="text-gray-400">info@familyphysician.com</span>
                  </li>
                  <li className="flex items-center">
                    <Clock className="h-5 w-5 text-primary-400 mr-2" />
                    <span className="text-gray-400">Mon-Fri: 8am-5pm</span>
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="border-t border-gray-800 mt-12 pt-8 text-center">
              <p className="text-gray-400 text-sm">
                © 2025 Family Physician Office. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </div>
    );
  };

  return (
    <div>
      {renderNavigation()}
      {renderPage()}
    </div>
  );
};